import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Ban, Clock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

/**
 * Shows every order placed by / for this parent-student family.
 * Parents + students can cancel pending pre-orders up to 4 PM IST the day before.
 * Item #3 + #19 — date range, status filter, scheduled-for column.
 */
export default function ParentMyOrders({ embedded = false }) {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("preorder"); // preorder | all | completed | cancelled
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/orders/me");
      setOrders(r.data || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return (orders || []).filter(o => {
      if (filter === "preorder" && o.type !== "preorder") return false;
      if (filter === "completed" && o.status !== "delivered" && o.status !== "completed") return false;
      if (filter === "cancelled" && o.status !== "cancelled") return false;
      if (o.scheduled_date) {
        if (fromDate && o.scheduled_date < fromDate) return false;
        if (toDate && o.scheduled_date > toDate) return false;
      }
      return true;
    }).sort((a, b) => String(b.scheduled_date || "").localeCompare(String(a.scheduled_date || "")));
  }, [orders, filter, fromDate, toDate]);

  const cancel = async (o) => {
    if (!window.confirm(`Cancel pre-order for ${o.scheduled_date}? Your wallet will be refunded ${inr(o.total)}.`)) return;
    try {
      await api.post(`/orders/${o.id}/cancel`);
      toast.success("Order cancelled. Wallet refunded.");
      load();
      refresh?.();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const statusBadge = (s) => {
    const map = {
      pending:    ["bg-sun/25 text-ink-700", Clock],
      preparing:  ["bg-sky/25 text-ink-700", Clock],
      ready:      ["bg-brand-100 text-brand-700", CheckCircle2],
      completed:  ["bg-brand-100 text-brand-700", CheckCircle2],
      delivered:  ["bg-brand-100 text-brand-700", CheckCircle2],
      cancelled:  ["bg-red-100 text-red-700", XCircle],
    };
    const [cls, Icon] = map[s] || ["bg-cream-200 text-ink-600", Clock];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
        <Icon size={12} /> {s}
      </span>
    );
  };

  const canCancel = (o) => {
    if (o.type !== "preorder" || o.status === "cancelled" || o.status === "delivered") return false;
    if (!o.scheduled_date) return false;
    const now = new Date();
    const target = new Date(o.scheduled_date + "T00:00:00+05:30");
    const tomorrowIst = new Date(now.getTime() + 86400000);
    tomorrowIst.setHours(0, 0, 0, 0);
    // past scheduled date — cannot cancel
    if (target < tomorrowIst && !(target.toISOString().slice(0, 10) === tomorrowIst.toISOString().slice(0, 10))) return false;
    const istHour = (now.getUTCHours() + 5.5 + 24) % 24;
    // If order is for tomorrow and it's past 4 PM IST, block
    if (target.toISOString().slice(0, 10) === tomorrowIst.toISOString().slice(0, 10) && istHour >= 16) return false;
    return true;
  };

  return (
    <ConditionalShell embedded={embedded} role={user?.role}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">My Orders</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Placed orders</h1>
            <p className="text-sm text-ink-500 mt-2">Track every pre-order, meal-plan tap, and POS purchase. Cancel pending pre-orders up to 4 PM the day before.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <select data-testid="order-filter" value={filter} onChange={e => setFilter(e.target.value)}
              className="px-4 py-2 rounded-full border border-border bg-white text-sm font-semibold text-ink">
              <option value="preorder">Pre-orders only</option>
              <option value="all">All orders</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="flex items-center gap-2 text-xs">
              <Calendar size={14} className="text-ink-500" />
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                data-testid="date-from"
                className="px-3 py-2 rounded-full border border-border bg-white text-sm" />
              <span className="text-ink-500">→</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                data-testid="date-to"
                className="px-3 py-2 rounded-full border border-border bg-white text-sm" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-8 text-center text-ink-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <AlertTriangle size={32} className="mx-auto text-ink-400 mb-3" />
            <div className="font-heading text-xl font-bold text-ink">No orders in this range</div>
            <p className="text-ink-500 text-sm mt-2">Pre-order for tomorrow's lunch — the canteen closes the pre-order window at 4 PM.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <table className="w-full text-sm" data-testid="my-orders-table">
              <thead className="bg-cream-200 text-left text-[11px] uppercase tracking-wider text-ink-600">
                <tr>
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Needed on</th>
                  <th className="px-4 py-3">Slot</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id} className="border-t border-border hover:bg-cream-100">
                    <td className="px-4 py-3 font-mono text-xs text-ink-700">{o.invoice_no || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-ink">
                      {o.scheduled_date || "—"}
                      {o.type === "preorder" && <span className="ml-2 px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase">Pre-order</span>}
                    </td>
                    <td className="px-4 py-3 capitalize text-ink-600">{o.meal_slot || "—"}</td>
                    <td className="px-4 py-3 text-ink-600 max-w-sm truncate" title={(o.items || []).map(i => `${i.qty}x ${i.name}`).join(", ")}>
                      {(o.items || []).slice(0, 2).map(i => `${i.qty}x ${i.name}`).join(", ")}
                      {(o.items || []).length > 2 && ` +${o.items.length - 2} more`}
                    </td>
                    <td className="px-4 py-3 uppercase text-[11px] font-semibold text-ink-500">{o.type}</td>
                    <td className="px-4 py-3 text-right font-heading font-bold text-ink">{inr(o.total)}</td>
                    <td className="px-4 py-3">{statusBadge(o.status)}</td>
                    <td className="px-4 py-3 text-right">
                      {canCancel(o) ? (
                        <button onClick={() => cancel(o)} data-testid={`cancel-order-${o.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100">
                          <Ban size={12} /> Cancel
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ConditionalShell>
  );
}

const ConditionalShell = ({ embedded, role, children }) => embedded
  ? <>{children}</>
  : <AppShell variant={role === "student" ? "student" : "parent"}>{children}</AppShell>;
